import React, { useState, useEffect, useRef, useCallback } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas }     from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { getRequest, postRequest, deleteRequest, pathname } from "@react/helpers/fetch"

import Card               from "@react/shared/ui/Card"
import Header             from "./components/Header"
import StatsStrip         from "./components/StatsStrip"
import Toolbar            from "./components/Toolbar"
import BlueprintsTable    from "./components/BlueprintsTable"
import Pagination         from "./components/Pagination"
import BlueprintFormModal from "./components/BlueprintFormModal"
import CsvExportModal     from "./components/CsvExportModal"

// Entry point for /quotes/qline_service_blueprints (the projects "Ouvrages"
// library). Server hands over the initial payload (see
// Quotes::QlineServiceBlueprints::SetParams). State changes — search, sort,
// page — refetch the same controller action as JSON and swap data in place.
const DEBOUNCE_MS = 300

const App = ({ data: initialData }) => {
  const [data,      setData]      = useState(initialData)
  const [search,    setSearch]    = useState(initialData.search || "")
  const [page,      setPage]      = useState(initialData.pagination?.current_page || 1)
  const [order,     setOrder]     = useState(initialData.order)
  const [direction, setDirection] = useState(initialData.direction)
  const [loading,   setLoading]   = useState(false)

  // Modal state: { mode: "create" | "edit", blueprintId?: number } | null
  const [modalState,    setModalState]    = useState(null)
  const [csvExportOpen, setCsvExportOpen] = useState(false)

  const indexUrl     = initialData.urls.index
  const didMountRef  = useRef(false)
  const debounceRef  = useRef(null)
  const requestIdRef = useRef(0)

  const fetchIndex = useCallback(() => {
    const requestId = ++requestIdRef.current
    const params    = { search, page, order, direction }

    setLoading(true)
    return getRequest(pathname({ path: indexUrl, format: "json", params })).then(json => {
      if (requestId !== requestIdRef.current) return
      setData(json)
      setLoading(false)
      window.history.replaceState(null, "", pathname({ path: indexUrl, params }))
    })
  }, [indexUrl, search, page, order, direction])

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(fetchIndex, DEBOUNCE_MS)
    return () => clearTimeout(debounceRef.current)
  }, [fetchIndex])

  const handleSearchChange = value => { setSearch(value); setPage(1) }
  const handleSortChange   = (nextOrder, nextDirection) => {
    setOrder(nextOrder); setDirection(nextDirection); setPage(1)
  }

  const openCreateModal = ()           => setModalState({ mode: "create" })
  const openEditModal   = blueprintId  => setModalState({ mode: "edit", blueprintId })
  const closeModal      = ()           => setModalState(null)
  const handleSaved     = ()           => fetchIndex()

  const handleExport = () => setCsvExportOpen(true)

  const handleDelete = blueprint => {
    if (!window.confirm(I18n.t("quotes.qline_service_blueprints.index.delete_confirm", { name: blueprint.name }))) return

    deleteRequest(blueprint.destroy_url)
      .then(response => {
        if (response && response.errors) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors))
          return
        }
        flashNotice(I18n.t("quotes.qline_service_blueprints.index.deleted"))
        fetchIndex()
      })
      .catch(() => flashAlert(I18n.t("quotes.qline_service_blueprints.index.network_error")))
  }

  // "Duplicate" pre-fills the modal from the source's detail JSON and saves
  // as a fresh record — user gets to review/edit before persisting.
  const handleDuplicate = blueprint => {
    setModalState({ mode: "duplicate", blueprintId: blueprint.id })
  }

  // Per-blueprint URLs templated off a sample row's destroy_url. Falls back
  // to indexUrl when the library is empty.
  const blueprintBaseUrl = id => {
    const template = data.blueprints[0]?.destroy_url || `${indexUrl}/${id}`
    return template.replace(/\/\d+(?=(?:\?|$))/, `/${id}`)
  }
  const showUrl      = modalState?.blueprintId ? `${blueprintBaseUrl(modalState.blueprintId)}.json` : null
  const saveUrl      = `${indexUrl}.json`
  const updateUrlFor = id => `${blueprintBaseUrl(id)}.json`
  const detailUrlFor   = id => `${blueprintBaseUrl(id)}.json`

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-[1500px] mx-auto px-8 py-8">
        <Header onNewClick={openCreateModal} onExport={handleExport} />
        <StatsStrip stats={data.stats} />
        <Card className={`rounded-lg relative ${loading ? "opacity-75" : ""}`}>
          <Toolbar
            search         = {search}
            onSearchChange = {handleSearchChange}
            order          = {order}
            direction      = {direction}
            onSortChange   = {handleSortChange}
          />
          <BlueprintsTable
            blueprints  = {data.blueprints}
            onEdit      = {openEditModal}
            onDuplicate = {handleDuplicate}
            onDelete    = {handleDelete}
          />
          <Pagination
            pagination   = {data.pagination}
            onPageChange = {setPage}
          />
        </Card>
      </div>

      <BlueprintFormModal
        isOpen            = {modalState !== null}
        mode              = {modalState?.mode}
        blueprintId       = {modalState?.blueprintId}
        showUrl           = {showUrl}
        saveUrl           = {saveUrl}
        updateUrlFor      = {updateUrlFor}
        detailUrlFor      = {detailUrlFor}
        enabledCategories = {data.enabled_cost_categories}
        onClose           = {closeModal}
        onSaved           = {handleSaved}
      />

      <CsvExportModal
        isOpen       = {csvExportOpen}
        onClose      = {() => setCsvExportOpen(false)}
        csvUrl       = {data.urls.csv}
        search       = {search}
        ids          = {data.ids || []}
        count        = {data.pagination?.total_count ?? 0}
        defaultEmail = {data.current_user_email}
      />
    </div>
  )
}

export default App
